// ./src/utils/api/RaceTimingService.ts
//
// Race timing (per-bib start / finish / duration) service. OpResult-native.
// Standard CRUD for manual edits + two custom mutations: `start` stamps a shared
// wave start on every bib in a ticket type / guest group / selection, and
// `finish` stamps one bib's finish (idempotent; safe to fire-and-forget over a
// flaky network — the client passes occurredAt captured at the tap). See
// em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";
import { RaceStartSource } from "./raceTimingConstants";

// =============================================================================
// GraphQL
// =============================================================================

const RACE_TIMING_FIELDS = `
  id
  eventId
  accountId
  bibNumber
  eventTicketId
  eventGuestId
  participantName
  groupRef
  groupKind
  groupLabel
  startedAt
  finishedAt
  quitAt
  durationMs
  rights
  createdBy
  createdAt
  updatedAt
`;

const RACE_TIMING_LIST_QUERY = gql`
  query EventRaceTimingList($filter: EventRaceTimingFilter, $params: PaginationAndSorting) {
    eventRaceTimingList(filter: $filter, params: $params) {
      code
      data { ${RACE_TIMING_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// ROADMAP #112 — the results page PAGES the leaderboard: the server orders it (`leaderboard`), filters it (`raceState`,
// waves, search), numbers the finishers (`place` — over the whole race, never the page) and counts it (`total`). ⚠ Its own
// query: the tracking console and the page editor read the plain list, and a field the gateway has not composed yet
// would fail THEIR reads too.
const RACE_RESULTS_PAGE_QUERY = gql`
  query RaceResultsPage($filter: EventRaceTimingFilter, $params: PaginationAndSorting) {
    eventRaceTimingList(filter: $filter, params: $params) {
      code
      total
      data { ${RACE_TIMING_FIELDS} place }
      errors { name errors warnings }
    }
  }
`;

const RACE_RESULTS_SUMMARY_QUERY = gql`
  query RaceResultsSummary($params: RaceResultsSummaryInput) {
    eventRaceTimingSummary(params: $params) {
      code
      data { eventId total finished running quit notStarted waves { groupRef groupLabel count } }
      errors { name errors warnings }
    }
  }
`;

const RACE_TIMING_GET_QUERY = gql`
  query EventRaceTimingGet($id: ID) {
    eventRaceTimingGet(id: $id) {
      code
      data { ${RACE_TIMING_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RACE_TIMING_CREATE_MUTATION = gql`
  mutation EventRaceTimingCreate($params: EventRaceTimingInput) {
    eventRaceTimingCreate(params: $params) {
      code
      data { ${RACE_TIMING_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RACE_TIMING_UPDATE_MUTATION = gql`
  mutation EventRaceTimingUpdate($where: EventRaceTimingWhereInput, $params: EventRaceTimingInput) {
    eventRaceTimingUpdate(where: $where, params: $params) {
      code
      data { ${RACE_TIMING_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RACE_TIMING_REMOVE_MUTATION = gql`
  mutation EventRaceTimingRemove($where: EventRaceTimingWhereInput) {
    eventRaceTimingRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const RACE_TIMING_START_MUTATION = gql`
  mutation EventRaceTimingStart($params: RaceTimingStartInput) {
    eventRaceTimingStart(params: $params) {
      code
      data { ${RACE_TIMING_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RACE_TIMING_FINISH_MUTATION = gql`
  mutation EventRaceTimingFinish($params: RaceTimingFinishInput) {
    eventRaceTimingFinish(params: $params) {
      code
      data { ${RACE_TIMING_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RACE_TIMING_QUIT_MUTATION = gql`
  mutation EventRaceTimingQuit($params: RaceTimingFinishInput) {
    eventRaceTimingQuit(params: $params) {
      code
      data { ${RACE_TIMING_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventRaceTiming schema)
// =============================================================================

export interface RaceTiming {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  bibNumber?: string | null;
  eventTicketId?: string | null;
  eventGuestId?: string | null;
  participantName?: string | null;
  groupRef?: string | null;
  groupKind?: string | null;
  groupLabel?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  quitAt?: string | null;
  durationMs?: number | null;
  /** A finished bib's OVERALL place in the race (the server's — never the page's or the search's); results page only. */
  place?: number | null;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface RaceTimingInput {
  eventId?: string | null;
  bibNumber?: string | null;
  eventTicketId?: string | null;
  eventGuestId?: string | null;
  participantName?: string | null;
  groupRef?: string | null;
  groupKind?: string | null;
  groupLabel?: string | null;
  /** ISO — set / null to correct a wave start. */
  startedAt?: string | null;
  /** ISO — set / null to correct a finish. */
  finishedAt?: string | null;
  /** ISO — set / null to correct a quit / DNF. */
  quitAt?: string | null;
}

export interface RaceTimingFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  groupRef?: string[] | null;
  groupKind?: string[] | null;
  bibNumber?: string[] | null;
  searchKeyword?: string | null;
  /** ROADMAP #112 — where a bib stands (one or several). */
  raceState?: RaceState[] | null;
  /** Order as the results leaderboard (finished by time → running → quit → not started). */
  leaderboard?: boolean | null;
}

export const RACE_STATE = { FINISHED: "finished", RUNNING: "running", QUIT: "quit", NOT_STARTED: "not_started" } as const;
export type RaceState = (typeof RACE_STATE)[keyof typeof RACE_STATE];

/** The results header over the WHOLE race (the paged list cannot count it). */
export interface RaceResultsSummary {
  eventId?: string | null;
  total: number;
  finished: number;
  running: number;
  quit: number;
  notStarted: number;
  waves: { groupRef: string; groupLabel?: string | null; count: number }[];
}

export interface RaceTimingWhereInput {
  id?: string | string[];
}

export interface RaceStartInput {
  eventId: string;
  sourceKind: RaceStartSource;
  /** The ticket type / guest group to start (ticket_type / guest_group sources). */
  groupRef?: string | null;
  /** The bibs to start (selection source). */
  bibNumbers?: string[] | null;
  /** When the wave started (ISO, captured on the timekeeper's device). */
  occurredAt: string;
  /** Restart bibs that already have a start (default false). */
  overwrite?: boolean;
}

export interface RaceFinishInput {
  eventId: string;
  bibNumber: string;
  /** When the bib crossed the line (ISO, captured on the timekeeper's device). */
  occurredAt: string;
  /** Overwrite an existing finish (default false — a double-scan keeps the first). */
  overwrite?: boolean;
}

// =============================================================================
// Service
// =============================================================================

export class RaceTimingService {
  static async list(filter?: RaceTimingFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(RACE_TIMING_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /** One page of the results leaderboard, with `place` and `total` (ROADMAP #112). */
  static async resultsPage(filter: RaceTimingFilter, pagination: Pagination): Promise<OpResult> {
    return ApiService.executeQuery(
      RACE_RESULTS_PAGE_QUERY,
      { filter: { ...filter, leaderboard: true }, params: { pagination } },
      { fetchPolicy: "network-only" }
    );
  }

  /** The results header for one event: bibs per state and the waves. */
  static async resultsSummary(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(RACE_RESULTS_SUMMARY_QUERY, { params: { eventId } }, { fetchPolicy: "network-only" });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(RACE_TIMING_GET_QUERY, { id });
  }

  static async create(params: RaceTimingInput): Promise<OpResult> {
    return ApiService.executeMutation(RACE_TIMING_CREATE_MUTATION, { params });
  }

  static async update(where: RaceTimingWhereInput, params: RaceTimingInput): Promise<OpResult> {
    return ApiService.executeMutation(RACE_TIMING_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(RACE_TIMING_REMOVE_MUTATION, { where: { id } });
  }

  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(RACE_TIMING_REMOVE_MUTATION, { where: { id: ids } });
  }

  /** Stamp a shared start on every bib in a ticket type / guest group / selection. */
  static async start(params: RaceStartInput): Promise<OpResult> {
    return ApiService.executeMutation(RACE_TIMING_START_MUTATION, { params });
  }

  /** Stamp a finish on one bib (idempotent). Fire-and-forget safe. */
  static async finish(params: RaceFinishInput): Promise<OpResult> {
    return ApiService.executeMutation(RACE_TIMING_FINISH_MUTATION, { params });
  }

  /** Mark one bib as quit / DNF (idempotent). Fire-and-forget safe. */
  static async quit(params: RaceFinishInput): Promise<OpResult> {
    return ApiService.executeMutation(RACE_TIMING_QUIT_MUTATION, { params });
  }
}
